import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Input, Spin, theme } from 'antd';
import type { InputRef } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { getTaxonomy } from '../services/TaxonomyService';
import { getAccessoryTaxonomy } from '../services/AccessoryTaxonomyService';
import { getActiveCategories } from '../services/HeavyDutyMachineryService';
import { buildServiceCategoryTaxonomy } from '../helpers/serviceCategoryTaxonomy';
import { TRANSLATIONS } from '../constants/translations';
import { translate } from '../helpers/translationHelper';

// Structural shape both ITaxonomySector (Sector/MachineryFamily/MachineryType)
// and IAccessoryCategory (AccessoryCategory/AccessoryFamily/AccessoryType)
// satisfy -- see this component's own `taxonomySource` prop doc comment.
// This screen never reads MachineryType's own extra `isAttachment` field,
// so the two taxonomies are interchangeable here.
interface CategoryScreenType {
  code: string;
  name: string;
}
interface CategoryScreenFamily {
  id: number;
  name: string;
  types: CategoryScreenType[];
}
interface CategoryScreenTopLevel {
  families: CategoryScreenFamily[];
}

interface HomeSearchCategoryScreenProps {
  initialQuery: string;
  onSelect: (category: string) => void;
  // Machinery's own "Tipo de maquinaria" narrows the full taxonomy down to
  // whatever MachineryType.Code values the catalog's own live
  // getActiveCategories reports (a currently-published listing exists for
  // it) -- see that function's own doc comment. Operator reuses this exact
  // same screen (and the machinery taxonomy) for its own "Tipo de
  // operario", but "currently has an active published listing" isn't a
  // real concept there, so it leaves this false and searches the whole
  // taxonomy as-is.
  narrowToActive?: boolean;
  placeholder?: string;
  // 'machinery' (default) fetches the Sector/MachineryFamily/MachineryType
  // taxonomy (see ITaxonomy.ts) -- what every mode except Accessory used
  // until this taxonomy pair's own AccessoryType existed. 'accessory'
  // fetches the separate AccessoryCategory/AccessoryFamily/AccessoryType
  // taxonomy instead (see IAccessoryTaxonomy.ts) -- what an accessory IS
  // (martillo hidráulico, cazo...), not which machine it fits. 'service'
  // is the trades catalogue, and is the one that is NOT fetched: it is a
  // small curated list living on this side (see
  // buildServiceCategoryTaxonomy), shaped the same three levels so this
  // screen needs no second code path for it. Never combined with
  // narrowToActive (not a concept any of them needs here beyond Machinery's
  // own case above).
  taxonomySource?: 'machinery' | 'accessory' | 'service';
}

/**
 * The mobile search overlay's "Tipo de maquinaria/accesorio/operario"
 * subscreen -- same plain in-page list pattern as HomeSearchLocationScreen
 * (see that component's own doc comment on why: antd's popup z-index sits
 * below this overlay's own). Unlike location search, the candidate list is
 * the full 3-level taxonomy (see `taxonomySource`'s own doc comment)
 * flattened to its leaf types and grouped by family header -- the same
 * "don't show every machine flat at once" reasoning TaxonomyPicker's own
 * doc comment gives, just without TaxonomyPicker's own top-level/Family
 * drill-down (a single scrollable, already-searchable list reads better
 * here than another modal
 * inside this already-modal-like overlay). Re-fetches if the app language
 * changes mid-session, same as TaxonomyPicker/useTaxonomy.
 */
const HomeSearchCategoryScreen: React.FC<HomeSearchCategoryScreenProps> = ({ initialQuery, onSelect, narrowToActive, placeholder, taxonomySource = 'machinery' }) => {
  const { t, i18n } = useTranslation();
  const { token } = theme.useToken();
  const [query, setQuery] = useState(initialQuery);
  const inputRef = useRef<InputRef>(null);
  const [sectors, setSectors] = useState<CategoryScreenTopLevel[] | null>(null);
  // null while loading/not applicable; a (possibly empty) Set once resolved
  // -- see the `narrowToActive` prop's own doc comment. A fetch failure
  // falls back to the same empty Set as a genuinely empty catalog rather
  // than a separate error state, same as before.
  const [activeCategories, setActiveCategories] = useState<Set<string> | null>(null);

  // Autofocus the moment this subscreen slides in -- same as HomeSearchLocationScreen.
  useEffect(() => {
    const raf = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    // Services resolve synchronously -- nothing to fetch, and nothing to fail.
    if (taxonomySource === 'service') {
      setSectors(buildServiceCategoryTaxonomy(t));
      return;
    }

    let cancelled = false;
    const fetchTaxonomy = taxonomySource === 'accessory' ? getAccessoryTaxonomy(i18n.language) : getTaxonomy(i18n.language);
    fetchTaxonomy
      .then((data) => {
        if (!cancelled) setSectors(data);
      })
      .catch(() => {
        if (!cancelled) setSectors([]);
      });
    return () => {
      cancelled = true;
    };
    // t is stable per language and i18n.language already covers a language
    // switch -- listing it would rebuild the service list on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language, taxonomySource]);

  useEffect(() => {
    if (!narrowToActive) return;
    let cancelled = false;
    getActiveCategories()
      .then((categories) => {
        if (!cancelled) setActiveCategories(new Set(categories));
      })
      .catch(() => {
        if (!cancelled) setActiveCategories(new Set());
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [narrowToActive]);

  const loading = sectors === null || (!!narrowToActive && activeCategories === null);

  // Every {family, type} row across the whole taxonomy, narrowed to
  // getActiveCategories when asked (see `narrowToActive`'s own doc
  // comment), BEFORE the text filter below ever runs -- same "narrow first,
  // filter second" order the old CATEGORY_OPTIONS-based version used.
  const availableRows = useMemo(() => {
    if (!sectors) return [];
    const rows: { family: CategoryScreenFamily; type: CategoryScreenType }[] = [];
    sectors.forEach((sector) =>
      sector.families.forEach((family) =>
        family.types.forEach((type) => {
          if (!narrowToActive || activeCategories?.has(type.code)) rows.push({ family, type });
        })
      )
    );
    return rows;
  }, [sectors, narrowToActive, activeCategories]);

  const results = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return availableRows;
    return availableRows.filter((row) => row.type.name.toLowerCase().includes(needle));
  }, [query, availableRows]);

  // Grouped by family so the list reads as a browsable catalog, not one
  // undifferentiated wall of 83 rows -- same grouping TaxonomyPicker's own
  // search mode uses.
  const groups = useMemo(() => {
    const list: { family: CategoryScreenFamily; rows: { family: CategoryScreenFamily; type: CategoryScreenType }[] }[] = [];
    results.forEach((row) => {
      let group = list.find((g) => g.family.id === row.family.id);
      if (!group) {
        group = { family: row.family, rows: [] };
        list.push(group);
      }
      group.rows.push(row);
    });
    return list;
  }, [results]);

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <div style={{ padding: '4px 20px 12px', flexShrink: 0 }}>
        <Input
          ref={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={placeholder ?? translate(t, TRANSLATIONS.HOMEPAGE_SEARCH_CATEGORY_PLACEHOLDER)}
          prefix={<SearchOutlined style={{ color: token.colorTextTertiary }} />}
          allowClear
          size="large"
          style={{ borderRadius: 999 }}
          autoComplete="off"
        />
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '0 20px 20px' }}>
        {loading && (
          <div style={{ textAlign: 'center', padding: 16 }}>
            <Spin size="small" />
          </div>
        )}

        {!loading && results.length === 0 && (
          <div style={{ textAlign: 'center', padding: 24, color: token.colorTextTertiary, fontSize: 14 }}>
            {translate(t, query.trim() ? TRANSLATIONS.HEAVY_DUTY_MACHINERY_SEARCH_NO_RESULTS : TRANSLATIONS.HOMEPAGE_SEARCH_CATEGORY_EMPTY)}
          </div>
        )}

        {!loading &&
          groups.map((group) => (
            <div key={group.family.id} style={{ marginBottom: 4 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: token.colorTextTertiary, textTransform: 'uppercase', letterSpacing: 0.5, padding: '14px 16px 6px' }}>
                {group.family.name}
              </div>
              {group.rows.map(({ type }, index) => (
                <button
                  key={type.code}
                  type="button"
                  onClick={() => onSelect(type.code)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    padding: '12px 16px',
                    border: 'none',
                    background: 'none',
                    textAlign: 'left',
                    cursor: 'pointer',
                    borderBottom: index < group.rows.length - 1 ? `1px solid ${token.colorBorderSecondary}` : 'none',
                  }}
                >
                  <span style={{ fontSize: 15, color: token.colorText }}>{type.name}</span>
                </button>
              ))}
            </div>
          ))}
      </div>
    </div>
  );
};

export default HomeSearchCategoryScreen;
